import { IconComponent } from '../icon/icon.component';
import { SelectComponent } from '../select/select.component';
import {
	Component,
	EventEmitter,
	HostBinding,
	Input,
	OnChanges,
	Output,
	SimpleChanges,
} from '@angular/core';
import { GetProductsByCategoryOptions } from '@app/api/Product';
import { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { faSort } from '@fortawesome/pro-solid-svg-icons/faSort';
import { faSortDown } from '@fortawesome/pro-solid-svg-icons/faSortDown';
import { faSortUp } from '@fortawesome/pro-solid-svg-icons/faSortUp';

export interface SortOption {
	label?: string;
	column: GetProductsByCategoryOptions['sortBy'];
	func?: (a: unknown, b: unknown) => number;
	icons?: {
		neutral: IconDefinition;
		asc: IconDefinition;
		desc: IconDefinition;
	};
}

export interface SortChangeEvent {
	value: SortOption;
	direction: number;
}

export const enum SortDirection {
	Neutral = 0,
	Asc = 1,
	Desc = -1,
}

/** A component to display sort options. */
@Component({
	imports: [IconComponent, SelectComponent],
	selector: 'g-sorter',
	templateUrl: './sorter.component.html',
	styleUrls: ['./sorter.component.scss'],
})
export class SorterComponent implements OnChanges {
	@Input() readonly value?: string;
	@Input() direction: SortDirection = SortDirection.Neutral;
	@Input() readonly sortOptions: SortOption[] = [];
	@Output() readonly onChange = new EventEmitter();
	selectedSortOption?: SortOption;
	sortIcon = this.getIconForCurrentSort();
	@HostBinding('attr.title') get title(): string {
		return this.labelFormatter({ option: this.selectedSortOption?.column });
	}

	ngOnChanges(changes: SimpleChanges): void {
		let shouldUpdateIcon = false;
		if (changes.value && changes.value.currentValue !== changes.value.previousValue) {
			this.dropdownValueChanged(changes.value.currentValue as string, false);
			shouldUpdateIcon = true;
		}

		if (changes.direction && changes.direction.currentValue !== changes.direction.previousValue) {
			shouldUpdateIcon = true;
		}

		if (shouldUpdateIcon) {
			this.sortIcon = this.getIconForCurrentSort();
		}
	}

	dropdownValueChanged(selectedValue: string, emitChange = true): void {
		if (this.selectedSortOption?.column === selectedValue) return;
		this.selectedSortOption = this.sortOptions?.find((option) => option.column === selectedValue);
		if (emitChange) this.emitChange();
	}

	toggleSortDirection(): void {
		this.direction = (this.direction || SortDirection.Desc) * -1;
		this.emitChange();
	}

	emitChange(): void {
		this.onChange.emit({
			value: this.selectedSortOption,
			direction: this.direction || SortDirection.Asc,
		});
	}

	labelFormatter = ({ option }: { option?: unknown } = {}): string => {
		const selected = this.sortOptions?.find((opt) => opt.column === option);
		return selected?.label ?? '';
	};

	getIconForCurrentSort(): IconDefinition {
		const getCustomSortIcon = (key: 'asc' | 'desc' | 'neutral') => this.selectedSortOption?.icons?.[key];

		switch (this.direction) {
			case SortDirection.Asc:
				return getCustomSortIcon('asc') || faSortDown;
			case SortDirection.Desc:
				return getCustomSortIcon('desc') || faSortUp;
			default:
				return getCustomSortIcon('neutral') || faSort;
		}
	}
}
